import { siteThemeAtom } from "@/config.ts";
import { languageEnabled_store } from "@/featureFlags.ts";
import { autoSelectLanguage } from "@/modules/l10n/autoSelectLanguage.ts";
import { i18nDefaultOptions } from "@/modules/l10n/english.ts";
import type { ThemeName } from "@/modules/theme/allThemeNames.ts";
import { getEnvironment } from "@/utils/environment.ts";
import { atomWithLocalStorage } from "@/utils/jotai/atomStorage.ts";
import { getOwn, hasOwn } from "@warrenio/utils/collections/getOwnProperty";
import { atom } from "jotai/vanilla";
import { keys } from "remeda";

/**
 * ---- List of all supported languages ----
 *
 * Add new locales here.
 */
export const localeMessages = {
    // `en` is always loaded by default, so no need to import it
    en: null,
    et: () => import("@/locales/et.po"),
    es: () => import("@/locales/es.po"),
    fi: () => import("@/locales/fi.po"),
    fr: () => import("@/locales/fr.po"),
    bn: () => import("@/locales/bn.po"),
    hi: () => import("@/locales/hi.po"),
    id: () => import("@/locales/id.po"),
    th: () => import("@/locales/th.po"),

    "en-x-arr": () => import("@/locales/en-x-arr.po"),
    "zz-pseudo": () => import("@/locales/zz-pseudo.po"),
} satisfies Record<string, MessagesImporter | null>;

const globalEnabledLanguages: ValidLanguage[] = ["en"];

/** Some languages can be enabled by specific themes */
const themeEnabledLanguages: Partial<Record<ThemeName, ValidLanguage[]>> = {
    atomidata: ["fi"],
    dataxion: ["fr"],
    denvr: ["fr"],
    pilvio: ["et"],
    web1: ["fi"],
    tenbyte: ["bn"],
    hostzop: ["hi"],
    vdc: ["id"],
    readyidc: ["th"],
};

//#region Language atoms

const languagesForceEnabledAtom = atom((get) => get(languageEnabled_store) ?? getEnvironment() !== "production");

export const availableLanguagesAtom = atom((get): ValidLanguage[] => {
    if (get(languagesForceEnabledAtom)) {
        return keys(localeMessages);
    }
    const theme = get(siteThemeAtom);
    return [...globalEnabledLanguages, ...(getOwn(themeEnabledLanguages, theme) ?? [])];
});

/** Spec: Language selector is enabled when there are available languages or it has been force-enabled by feature flag */
export const languagesEnabledAtom = atom((get) => get(availableLanguagesAtom).length || get(languagesForceEnabledAtom));

/** User-selected language */
export const languageStoreAtom = atomWithLocalStorage<string | undefined>("language", undefined);

/** Language for displaying texts in the user interface */
export const languageAtom = atom((get): ValidLanguage => {
    if (!get(languagesEnabledAtom)) {
        return i18nDefaultOptions.locale;
    }

    const validLocales = get(availableLanguagesAtom);
    const language = get(languageStoreAtom) ?? autoSelectLanguage(validLocales);
    // Check if the persisted user language is valid (maybe the locale has been removed later).
    // If not, use the default locale.
    const isValid = !!language && hasOwn(localeMessages, language);
    return isValid ? (language as ValidLanguage) : i18nDefaultOptions.locale;
});

//#endregion

//#region Utility types
type MessagesImporter = () => Promise<{ messages: Record<string, string> }>;

/** Identifier of a supported language. */
export type ValidLanguage = keyof typeof localeMessages;
//#endregion
